import { isValidPhoneNumber } from "libphonenumber-js";
import { jwtDecode } from "jwt-decode";
import { TCredentials, TUser, TWorkflow } from "../types/context";
import moment from "moment";
import {
  GenericObjectArrayType,
  MonthlyTransaction,
  TActions,
} from "../types/common";
import {
  ACTIVATE,
  APPROVED,
  CHANNEL_COLORS,
  CLOSE,
  CONST_CREDENTIALS_KEY,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  DEACTIVATE,
  DEFERRED,
  DELETE,
  DRAFT,
  EDIT,
  FAILED,
  FILE_TYPE_DOC,
  FILE_TYPE_DOCX,
  FILE_TYPE_JPEG,
  FILE_TYPE_JPG,
  FILE_TYPE_PDF,
  FILE_TYPE_PNG,
  PENDING,
  PENDING_APPROVAL,
  POSTED,
  RECOMMENDED_FOR_APPROVAL,
  REJECTED,
  SENT_TO_BANK,
  SETTLED,
  STATUS_COLORS,
  SUBMITTED,
  VIEW,
} from "./constants";
import { BASE_API_URL } from "../config";
import { useAuth } from "../context/AuthContext";
import { renderSingleFile } from "../components/common/FileViewer";
import { axiosInstance } from "../hooks/common/useAxios";
import styled from "styled-components";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { openModalForm } from "./modalUtils";

export type Permission = { [key: string]: boolean };

export const setCredentials = (value: TCredentials) => {
  try {
    window.localStorage.setItem(CONST_CREDENTIALS_KEY, JSON.stringify(value));
  } catch (e) {
    // saving error
  }
};

export const removeCredentials = () => {
  try {
    localStorage.clear();
  } catch (e) {
    // saving error
  }
};

export const decodeUser = (token: string) => {
  try {
    const decoded: any = jwtDecode(token);
    const user_id = decoded.user_id;
    return { id: user_id, ...decoded.user };
  } catch (err) {
    // saving error
  }
};

export const getCredentials = () => {
  try {
    const res = window.localStorage.getItem(CONST_CREDENTIALS_KEY);
    return res ? JSON.parse(res) : null;
  } catch (e) {
    // saving error
  }
};

export const generateDocumentAuthor = (user: any) => ({
  author_first_name: user?.first_name,
  author_last_name: user?.last_name,
  author_email: user?.email,
  author_phone_number: user?.phone_number || "0709456322",
  created_by: user?.id,
});

export const getAndPrepareFiles = (
  form: any,
  fileFieldNames: string[],
  field: string = "",
) => {
  const uploadedFiles: any = [];
  fileFieldNames.forEach((item) => {
    if (form[item]) {
      uploadedFiles.push(form[item]);
      delete form[item];
    }
  });
  const prefix = field || "attachments";
  return {
    ...form,
    ...prepareAttachmentsPost(uploadedFiles, prefix),
  };
};

export const prepareAttachmentsPost = (
  attachments: any,
  field?: any,
  object?: any,
  noFile?: any,
) => {
  const prefix = field || "attachments" || "other";
  const extras = {};
  const atts = (attachments || []).filter((item: any) => noFile || item?.file);
  const inflated_data: any = [];
  atts.forEach((item: any) => {
    if (item?.file) {
      item?.file.forEach((it: any) => {
        inflated_data.push({ ...item, file: it });
      });
    } else {
      inflated_data.push(item);
    }
  });

  inflated_data
    .map((item: any) => ({ ...item, ...object }))
    .forEach((item: any, idx: any) => {
      Object.keys(item).forEach((key) => {
        extras[`${prefix}[${idx}]${key}`] = item[key];
      });
    });
  return extras;
};

export const prepareObjectFormdataPost = (parentObject, field) => {
  const extras = {};
  const object = parentObject[field];
  if (!object) {
    return parentObject;
  }
  Object.keys(object).forEach((key) => {
    extras[`${field}.${key}`] = object[key];
  });
  parentObject = { ...parentObject, ...extras };
  delete parentObject[field];
  return parentObject;
};
export const prepareObjectArrayFormdataPost = (parentObject, field) => {
  const extras = {};
  const object = parentObject[field];
  if (!object) {
    return parentObject;
  }
  object.forEach((item, idx) => {
    Object.keys(item).forEach((key) => {
      extras[`${field}[${idx}]${key}`] = item[key];
    });
  });
  parentObject = { ...parentObject, ...extras };
  delete parentObject[field];
  return parentObject;
};

export const validateEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase())
    ? null
    : "Please enter a valid email address";
};

export const validatePhoneNumber = (phoneNumber: string) => {
  return isValidPhoneNumber(`${phoneNumber}`)
    ? null
    : "Please enter a valid phone number";
};

export const validateConfirmPass = (
  password: string,
  confirmPassword: string,
) => {
  return password === confirmPassword ? null : "Both passwords should match";
};

export const generateErrorString = (error: any) => {
  let errorString = "";
  Object.keys(error || {}).forEach((key) => {
    errorString += ` ${error[key]}`;
  });
  return errorString;
};

export const timeToString = (minutes: number) => {
  let str = "";
  if (minutes <= 60) {
    str = `${minutes}mins`;
  } else if (minutes <= 1440) {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    str = `${hrs}hrs ${mins}mins`;
  } else {
    const days = Math.floor(minutes / 1440);
    const mins = Math.floor(minutes % 1440);
    const hrs = Math.floor(mins / 60);
    str = `${days}days ${hrs}hrs`;
  }

  return str;
};

export const breakColArray = (array: any[]) => {
  const col1Fields: any[] = [];
  const col2Fields: any[] = [];
  array.forEach((item, idx) => {
    if (idx % 2 === 0) {
      col1Fields.push(item);
    } else {
      col2Fields.push(item);
    }
  });
  return { col1Fields, col2Fields };
};

export const returnObject = (show: boolean, object: any) => {
  return show ? object : [];
};

export const beautify = (message?: string) => {
  return (message || "")
    .replaceAll("_", " ")
    .replaceAll("/", "")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const stringifyError = (message: any) => {
  if (!message) return "An unknown error occured";

  if (typeof message === "string") return message;

  let obj;
  if (Array.isArray(message) && message.length) {
    obj = String(message?.[0]);
  }

  return obj ? String(obj) : "An unknown error occured";
};

export const stringifyName = (user: TUser | null) => {
  return user
    ? `${user?.first_name} ${user?.last_name} ${
        user?.other_name ? user?.other_name : ""
      }`
    : "";
};

export const toPastTense = (verb: string) => {
  // List of irregular verbs (cast to object with string keys)
  const irregularVerbs = {
    go: "went",
    eat: "ate",
    drink: "drank",
    leave: "left",
    cancel: "cancelled",
    delete: "deleted",
    resend_invitation: "resent invitation for",
  } as const;

  // Check if the verb is in the list of irregular verbs
  if (verb in irregularVerbs) {
    return irregularVerbs[verb as keyof typeof irregularVerbs];
  }

  return verb + "ed";
};

export const returnArray = (data: any) => {
  return Array.isArray(data) ? data : [];
};

export const stringifyDate = (date: Date) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  // Check if the date stored in the variable 'date' is today's date
  if (date.getDate() === today.getDate()) {
    return "Today";
  } else if (date.getDate() === yesterday.getDate()) {
    return "Yesterday";
  } else {
    return moment(date).format("ddd MMM DD, YYYY");
  }
};

export const detailsCardView = (item: any) => {
  let new_item: any = {};
  Object.keys(item).map((key) => {
    if (!key.includes("-name"))
      new_item[key] =
        item[`${key}-name`] != null ? item[`${key}-name`] : item[key];
  });
  return new_item;
};

export const stringifyWeeklyDate = (date: Date) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  // Check if the date stored in the variable 'date' is today's date
  if (date.getDate() === today.getDate()) {
    return "Today";
  } else if (date.getDate() === yesterday.getDate()) {
    return "Yesterday";
  } else {
    return moment(date).format("ddd MMM DD, YYYY");
  }
};

export const computerLastWorkingDate = (date: string) => {
  // Get yesterday's date
  const lastDay = moment(date).subtract(1, "day");

  // Check if yesterday was a Sunday
  if (lastDay.day() === 0) {
    // Sunday is represented by 0
    lastDay.day(-2);
  }

  return lastDay;
};

export const computerNextWorkingDate = (date: string) => {
  const today = moment(date);

  // Check if this date is a Friday
  if (today.day() === 5) {
    today.add(3, "days");
  } else {
    today.add(1, "days");
  }

  return today;
};

export const getTitle = (action?: TActions, postfix?: string) => {
  switch (action) {
    case "create":
      return `Create ${postfix || ""}`;
    case "edit":
      return `Update ${postfix || ""}`;
    case "delete":
      return `Delete ${postfix || ""}`;
    case "activate":
      return "Activate User";
    case "ban":
      return "Deactivate User";
    case "resend_invitation":
      return "Resend Invitation";
    default:
      return "";
  }
};

export const getToastTitle = (action?: TActions, postfix?: string) => {
  switch (action) {
    case "create":
      return `created ${postfix || ""}`;
    case "edit":
      return `updated ${postfix || ""}`;
    case "delete":
      return `deleted ${postfix || ""}`;
    default:
      return "";
  }
};

export const sanitizeAction = (action?: TActions) => {
  switch (action) {
    case "create":
      return "create";
    case "edit":
      return "update";
    case "delete":
      return "delete";
    case "resend_invitation":
      return "resend invitation";
    default:
      return "";
  }
};

export const scoreStype = (value: number) => {
  if (value >= 70) {
    return "success";
  } else if (value >= 50 && value < 70) {
    return "ready";
  } else {
    return "danger";
  }
};

export const getQueryParams = (search: string) => {
  return Object.fromEntries(new URLSearchParams(search));
};

export const mondayThisWeek = () => {
  // Get current date
  const today = moment();

  // Calculate Monday of this week
  const mondayOfThisWeek = today.startOf("isoWeek");

  return mondayOfThisWeek.format("YYYY-MM-DD");
};

export const fridayThisWeek = () => {
  // Get current date
  const today = moment();

  // Calculate Friday of this week
  const fridayOfThisWeek = today.endOf("isoWeek").subtract(2, "days");

  return fridayOfThisWeek.format("YYYY-MM-DD");
};

export const goBackOneWeek = (date: string) => {
  const today = moment(date);
  // Calculate Monday of last week
  const lastWeek = today.subtract(1, "week");
  return lastWeek.format("YYYY-MM-DD");
};

export const stringifyAction = (action: TActions, identifier: string) => {
  switch (action) {
    case "delete":
      return `delete this ${identifier}`;
    default:
      return "";
  }
};

export const getActionToolTip = (action: TActions) => {
  switch (action) {
    case "delete":
      return "Delete";
    case "ban":
      return "Deactivate";
    default:
      return "";
  }
};

export const getPhotoSource = (photo: string) => {
  return photo ? `${BASE_API_URL}${photo}` : "";
};

export const beautifyStringWithUnderscore = (str: string) => {
  return str.replace(/_/g, " ");
};

export const greetings = () => {
  const hour = moment().hour();
  if (hour >= 0 && hour < 12) {
    return "Good morning";
  } else if (hour >= 12 && hour < 17) {
    return "Good afternoon";
  } else {
    return "Good evening";
  }
};

// export const createColumn = (id: any, hasFilter: boolean = false) => {
//   return {
//     id,
//     header: beautify(id),
//     ...returnObject(hasFilter, { filter: {} }),
//   };
// };

// export const createColumn = (
//   id: any,
//   hasFilter: boolean = false,
//   customHeader?: string,
// ) => {
//   return {
//     id,
//     header: customHeader || beautify(id),
//     ...returnObject(hasFilter, { filter: {} }),
//   };
// };

type FilterConfig = {
  type?: "text" | "select" | "date" | "number" | "boolean";
  options?: Array<{ label: string; value: any }>;
  placeholder?: string;
  single?: boolean;
  range?: boolean;
  rangeNames?: {
    startKey?: string;
    endKey?: string;
  };
  // Add more filter configuration options as needed
};

type ColumnOptions = {
  customHeader?: string;
  filterConfig?: FilterConfig;
};

export const createColumn = (
  id: any,
  hasFilterOrConfig: boolean | FilterConfig = false,
  options?: string | ColumnOptions,
) => {
  let header = beautify(id);
  let filterConfig: FilterConfig | undefined;

  if (typeof options === "string") {
    header = options;
  } else if (options && typeof options === "object") {
    if (options.customHeader) header = options.customHeader;
    filterConfig = options.filterConfig;
  }

  let filter: any = undefined;

  if (filterConfig) {
    filter = { ...filterConfig };
  } else if (hasFilterOrConfig === true) {
    filter = {}; // Default empty filter
  } else if (typeof hasFilterOrConfig === "object") {
    filter = { ...hasFilterOrConfig };
  }

  return {
    id,
    header,
    ...(filter ? { filter } : {}),
  };
};

export const formatDate = (value: any, format?: string) => {
  return moment(value).format(format || DATE_FORMAT);
};

export const formatTime = (value: any, format?: string) => {
  if (!value) return "";
  const time = moment(value, "HH:mm:ss");
  return time.isValid() ? time.format(format || "h:mm A") : "";
};

export const formatDateTime = (value: any, format?: string) => {
  if (!value) return "";
  const date = moment.parseZone(value);

  return date.isValid() ? date.format(format || DATE_TIME_FORMAT) : "";
};

export const getAgo = (value: any) => {
  return moment(value).fromNow();
};

export const numberFormat = (value: number) =>
  Number(value || 0).toLocaleString();

export function getRegister(id: string, status?: string) {
  const suffix = status ? `/${status}` : "";
  return `/register${id}${suffix}`;
}

export function getForm(source_id: string, id?: string) {
  if (id) return `${source_id}/update/${id}`;
  return `${source_id}/create`;
}

export function getDetailsView(source_id: string, id: string) {
  return `${source_id}/view/${id}`;
}

export const getStatusColor = (status: string) => {
  switch (status) {
    case PENDING:
    case SUBMITTED:
      return "gold-inverse";
    case PENDING_APPROVAL:
      return "gold"; // softer than gold-inverse
    case RECOMMENDED_FOR_APPROVAL:
    case POSTED:
      return "geekblue";
    case SENT_TO_BANK:
      return "blue"; // lighter cyan-blue, not inverse
    case REJECTED:
    case FAILED:
      return "red-inverse";
    case APPROVED:
    case "current":
      return "green-inverse";
    case SETTLED:
      return "cyan"; // teal, distinct from plain green
    case DEFERRED:
      return "orange";
    case DRAFT:
      return "default"; // neutral gray — just "not active yet"
    default:
      return "";
  }
};

export const createSelectItem = (id: string, name?: string) => {
  return { id, name: name || capitalize(beautify(id)) };
};

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const createEditAction = (url: string, id: string) => {
  return {
    label: EDIT,
    to: getForm(url, id),
  };
};

export const createViewAction = (url: string, id: string) => {
  return {
    label: VIEW,
    to: getDetailsView(url, id),
  };
};

export const createDeleteAction = (url: string, identifier: string) => {
  return {
    label: DELETE,
    deleteUrl: url,
    deleteMessage: `Are you sure you want to delete ${identifier}?`,
  };
};

export const createDeactivateAction = (
  url: string,
  identifier: string,
  fields: Record<string, any> = {},
  options: {
    method?: "PATCH" | "POST" | "PUT";
    showReasonField?: boolean;
    customParams?: Record<string, any>;
  } = {},
) => ({
  label: DEACTIVATE,
  deleteUrl: url,
  deleteMessage: `Are you sure you want to deactivate ${identifier}?`,
  showReasonField: options?.showReasonField || false,
  method: options.method || "PATCH",
  fields: {
    ...fields,
    ...(options.customParams || {}),
  },
});

// export const createActivateAction = (
//   url: string,
//   identifier: string,
//   fields?: Record<string, any>,
// ) => {
//   return {
//     label: ACTIVATE,
//     deleteUrl: url,
//     deleteMessage: `Are you sure you want to activate ${identifier}?`,
//     ...(fields && { fields }),
//   };
// };

export const createActivateAction = (
  url: string,
  identifier: string,
  fields: Record<string, any> = {},
  options: {
    method?: "PATCH" | "POST" | "PUT";
  } = {},
) => ({
  label: ACTIVATE,
  deleteUrl: url,
  deleteMessage: `Are you sure you want to activate ${identifier}?`,
  fields,
  method: options.method || "PATCH",
});

export const createCloseAction = (url: string, identifier: string) => {
  return {
    label: CLOSE,
    deleteUrl: url,
    deleteMessage: `Are you sure you want to close ${identifier}?`,
  };
};

export const createCustomAction = (
  url: string,
  label: string,
  icon: any,
  handleCustom: () => void,
) => {
  return {
    label: label,
    to: url,
    icon,
    custom: true,
    handleCustom: () => handleCustom(),
  };
};

export const createModalFormAction = (
  source_id: string,
  label: string,
  icon: any,
  additionalOptions?: Record<string, any>,
) => {
  return {
    label,
    icon,
    custom: true,
    handleCustom: () => openModalForm({ source_id, ...additionalOptions }),
  };
};

export const createViewEdit = (
  url: string,
  id: string,
  shouldEdit: boolean = true,
) => {
  const views = [createViewAction(url, id)];
  shouldEdit && views.push(createEditAction(url, id));
  return views;
};

export function currentYear() {
  return new Date().getFullYear();
}

export function removeEmpty(array: [], key: string) {
  return array?.filter((val) => {
    return val[key] > 0;
  });
}

export const scaleFormatter = (value: any) => {
  let label = Intl.NumberFormat("en", { notation: "compact" }).format(value);
  return label;
};

export function getValidators(sections: any[]) {
  let validators: any = [];
  sections?.map((item) => {
    if (item.fields) validators = [...validators, ...item.fields];
  });
  return validators;
}

const removeUnusedFields = (form_data: any) => {
  if (form_data == null || typeof form_data !== "object") {
    return form_data;
  }
  const ignoredFields = [
    "id",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
    "deleted",
    "declaration",
    "attachments",
  ];
  Object.keys(form_data).forEach((key) => {
    if (ignoredFields.includes(key)) {
      delete form_data[key];
    }
  });
  return form_data;
};
export const objectWithUnsetKeys = (object, keys) => {
  const temp = { ...object };
  keys.forEach((item) => {
    delete temp[item];
  });
  return temp;
};
export const removeUnnecessaryFields = (data: any, keys: string[] = []) => {
  if (Array.isArray(data)) {
    return data.map((it: any) => removeUnusedFields(it));
  }
  const newData = removeUnusedFields(data);
  if (keys) {
    return objectWithUnsetKeys(newData, keys);
  }
  return newData;
};

export const useGetUserPermissions = () => {
  const { user }: any = useAuth();

  const permissions = user?.role?.permissions;

  const PermissionsArray = Object.entries(permissions || {})?.map(
    ([key, value]) => {
      return {
        [key]: value,
      };
    },
  );
  return PermissionsArray;
};

export const itemExists = (itemsArray: any[], itemsList: any[]) => {
  const flatValues = itemsArray.flatMap((obj) => Object.values(obj));
  return itemsList.some((item) => flatValues.includes(item));
};

export const isPdf = (file) => {
  return [FILE_TYPE_PDF].includes(file.split(".").pop());
};

export const isMSWord = (file) => {
  return [FILE_TYPE_DOCX, FILE_TYPE_DOC].includes(file.split(".").pop());
};

export const isImage = (file) => {
  return [FILE_TYPE_PNG, FILE_TYPE_JPEG, FILE_TYPE_JPG].includes(
    file.split(".").pop(),
  );
};

export const NoWrap = styled.div`
  white-space: nowrap !important;
`;

export const downloadThroughAnchorLink = (
  downloadUrl,
  fileName,
  params = {},
) => {
  return axiosInstance
    .get(downloadUrl, {
      responseType: "blob",
      params: {
        ...params,
      },
      headers: {
        Authorization: `Bearer ${getCredentials()?.access}`,
      },
    })
    .then((response) => {
      // create file link in browser's memory
      const href = URL.createObjectURL(response.data);

      // create "a" HTML element with href to file & click
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    });
};

export const displayDocuments = (docs) => {
  const st = {};
  Object.keys(docs || {})
    .filter((key) => docs[key])
    .map((key) => docs[key])
    .forEach((item) => {
      item.forEach((doc, idx) => {
        const caption = idx > 0 ? idx + 1 : "";
        st[`${doc.caption}_${caption}`] = renderSingleFile(doc);
      });
    });

  return st;
};

export const groupAttachmentsByCaption = (attachmentsArray) => {
  return (attachmentsArray || []).reduce((grouped, doc) => {
    const caption = doc.caption || "document";
    if (!grouped[caption]) {
      grouped[caption] = [];
    }
    grouped[caption].push(doc);
    return grouped;
  }, {});
};

export const getNonNullKeys = (
  obj: Record<string, any>,
  excludeKeys: string[] = [],
) => {
  return Object.keys(obj).filter(
    (key) => obj[key] !== null && !excludeKeys.includes(key),
  );
};

export const showObjectItems = (
  obj: Record<string, any>,
  excludeKeys: string[] = [],
) => {
  let cleanedObj: Record<string, any> = {};

  for (let key in obj) {
    if (
      obj[key] !== null &&
      typeof obj[key] !== "object" &&
      !excludeKeys.includes(key)
    ) {
      cleanedObj[key] = obj[key];
    }
  }

  return cleanedObj;
};

export const getResultOptions = (
  data: any[] | Record<string, any>,
  itemName: string | undefined,
  key?: string | undefined | null,
  otherItemName?: string | undefined | null,
) => {
  const results = Array.isArray(data) ? data : data?.results || [];
  return results.length
    ? results.map((item: GenericObjectArrayType) => ({
        id: key ? item[key] : item.id,
        name: beautify(
          `${(itemName && item[itemName]) || ""} ${
            (otherItemName && item[otherItemName]) || ""
          }`.trim() || item.name,
        ),
        data: item,
      }))
    : [];
};

export const getPermissionsFromObject = (item: any): Permission[] => {
  if (!item || typeof item !== "object") return [];
  const permissions: Permission[] = Object.entries(item)
    .filter(([key]) => key.startsWith("can_"))
    .map(([key, value]) => ({ [key]: Boolean(value) }));

  return permissions;
};

export const permissionObj = (role_permissions: any) => {
  const permissions = role_permissions?.map((item: any) => {
    const key = Object.keys(item)[0];
    return {
      label: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      key,
      value: key,
      checked: item[key],
    };
  });

  return permissions;
};

export const removeNullFields = (obj: any) => {
  if (!obj || typeof obj !== "object") return {};
  const newObj = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== null && obj[key] !== "") {
      newObj[key] = obj[key];
    }
  });
  return newObj;
};

export const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num);
};

export const roundNumber = (num: number) => {
  const rounded = Math.round(num);
  return new Intl.NumberFormat().format(rounded);
};

export const formatFullTimestamp = (date) => {
  return new Intl.DateTimeFormat("en-UG", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(date);
};

export const formatLastUpdated = (date) => {
  return new Intl.DateTimeFormat("en-UG", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-UG", {
    style: "currency",
    currency: "UGX",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const useUserRole = () => {
  const { user } = useAuth();
  return user?.role?.name || null;
};

/**
 * @param {TWorkflow[]} workflows
 * @param {string} name
 * @param {number} [level]
 * @returns {boolean}
 */
export const hasWorkflow = (
  workflows: TWorkflow[] = [],
  name: string,
  level?: number,
): boolean => {
  if (!Array.isArray(workflows) || !name) return false;

  return workflows.some((w) => {
    const matchesName = w.workflow_name === name;
    const matchesLevel =
      level !== undefined ? w.scrutiny_level === level : true;
    return matchesName && matchesLevel;
  });
};

dayjs.extend(isSameOrBefore);

export const getDatesInRange = (start: string, end: string) => {
  const dates: string[] = [];
  let current = dayjs(start);
  const last = dayjs(end);

  while (current.isSameOrBefore(last)) {
    dates.push(current.format("YYYY-MM-DD"));
    current = current.add(1, "day");
  }

  return dates;
};

/**
 * Flatten an object recursively for rendering
 * @param obj Object to flatten
 * @param parentKey Optional prefix for nested keys
 * @returns Array of { key, value } pairs
 */
export const flattenObjectForRender = (
  obj: Record<string, any>,
  parentKey: string = "",
): { key: string; value: any }[] => {
  if (!obj || typeof obj !== "object") return [];

  const result: { key: string; value: any }[] = [];

  Object.entries(obj).forEach(([key, value]) => {
    const newKey = parentKey ? `${parentKey}.${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      result.push(...flattenObjectForRender(value, newKey));
    } else if (Array.isArray(value)) {
      value.forEach((item, idx) => {
        if (typeof item === "object") {
          result.push(...flattenObjectForRender(item, `${newKey}[${idx}]`));
        } else {
          result.push({ key: `${newKey}[${idx}]`, value: item });
        }
      });
    } else {
      result.push({ key: newKey, value });
    }
  });

  return result;
};

/**
 * Detects the network provider (MTN or Airtel) based on phone number prefix
 * @param phoneNumber - Phone number in format 256787486588 or 0787486588
 * @returns "MTN" | "AIRTEL" | "UNKNOWN"
 */
export const detectNetworkProvider = (
  phoneNumber: string,
): "MTN" | "AIRTEL" | "UNKNOWN" => {
  if (!phoneNumber) return "UNKNOWN";

  // Remove any spaces, dashes, or special characters
  const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, "");

  // Normalize the number to start with 256
  let normalizedNumber = cleanNumber;
  if (cleanNumber.startsWith("0")) {
    // Convert 0787486588 to 256787486588
    normalizedNumber = "256" + cleanNumber.substring(1);
  } else if (cleanNumber.startsWith("+256")) {
    // Convert +256787486588 to 256787486588
    normalizedNumber = cleanNumber.substring(1);
  }

  const MTN_PREFIXES = ["25677", "25678", "25676", "25679"];
  const AIRTEL_PREFIXES = ["25670", "25675", "25674"];

  // Check MTN prefixes
  if (MTN_PREFIXES.some((prefix) => normalizedNumber.startsWith(prefix))) {
    return "MTN";
  }

  // Check Airtel prefixes
  if (AIRTEL_PREFIXES.some((prefix) => normalizedNumber.startsWith(prefix))) {
    return "AIRTEL";
  }

  return "UNKNOWN";
};
export const disableFutureDates = (current: any) => {
  return current && current > dayjs().endOf("day");
};

export const buildMonthlyStatusBarData = (
  results: MonthlyTransaction[],
  metric: "total_amount" | "total_count" = "total_amount",
) => {
  const months = Array.from(
    new Set(results.map((r) => `${r.month_name} ${r.year_value}`)),
  );

  // unique statuses
  const statuses = Array.from(new Set(results.map((r) => r.status)));

  // build MUI BarChart series
  const series = statuses.map((status) => ({
    label: status,
    data: months.map((month) => {
      const record = results.find(
        (r) =>
          `${r.month_name} ${r.year_value}` === month && r.status === status,
      );

      if (!record) return 0;

      return metric === "total_amount"
        ? Number(record.total_amount)
        : record.total_count;
    }),
  }));

  return { months, series };
};

//Transform Chart data

export const transformDailyData = (
  results: any[],
  metric: "total_amount" | "total_count" = "total_amount",
  groupBy: "status" | "channel" = "status",
) => {
  const days = Array.from(
    new Set(results.map((r) => r.day_of_month)),
  ) as number[];
  days.sort((a, b) => a - b);

  const groups = Array.from(new Set(results.map((r) => r[groupBy])));

  const series = groups.map((group) => ({
    label: group,
    data: days.map((day) => {
      const item = results.find(
        (r) => r.day_of_month === day && r[groupBy] === group,
      );
      if (!item) return 0;

      return metric === "total_amount"
        ? Number(item.total_amount)
        : item.total_count;
    }),
    color: groupBy === "status" ? STATUS_COLORS[group] : CHANNEL_COLORS[group],
  }));

  const xLabels = results?.map((data) => {
    const { month_name, day_of_month } = data || {};
    const dateString = `${day_of_month} ${month_name?.substring(0, 3)}`;
    return dateString;
  });

  return { xLabels, series };
};

export const transformWeeklyData = (
  results: any[],
  metric: "total_amount" | "total_count" = "total_amount",
  groupBy: "status" | "channel" = "status",
) => {
  // Derive week_of_month from day_of_month (1-7 = Week 1, 8-14 = Week 2, etc.)
  const resultsWithWeek = results.map((r) => ({
    ...r,
    week_of_month: Math.ceil(r.day_of_month / 7),
  }));

  const weeks = Array.from(
    new Set(resultsWithWeek.map((r) => r.week_of_month)),
  ) as number[];
  weeks.sort((a, b) => a - b);

  const groups = Array.from(new Set(resultsWithWeek.map((r) => r[groupBy])));

  const series = groups.map((group) => ({
    label: group,
    data: weeks.map((week) => {
      // Sum up all days that fall in this week for this group
      const items = resultsWithWeek.filter(
        (r) => r.week_of_month === week && r[groupBy] === group,
      );
      if (!items.length) return 0;

      return metric === "total_amount"
        ? items.reduce((sum, item) => sum + Number(item.total_amount), 0)
        : items.reduce((sum, item) => sum + item.total_count, 0);
    }),
    color: groupBy === "status" ? STATUS_COLORS[group] : CHANNEL_COLORS[group],
  }));

  const xLabels = weeks.map((w) => `Week ${w}`);

  return { xLabels, series };
};

export const transformMonthlyData = (
  results: any[],
  metric: "total_amount" | "total_count" = "total_amount",
  groupBy: "status" | "channel" = "status",
) => {
  const MONTH_ORDER = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const months = Array.from(new Set(results.map((r) => r.month_name)));
  months.sort((a, b) => MONTH_ORDER.indexOf(a) - MONTH_ORDER.indexOf(b));

  const groups = Array.from(new Set(results.map((r) => r[groupBy])));

  const series = groups.map((group) => ({
    label: group,
    data: months.map((month) => {
      const item = results.find(
        (r) => r.month_name === month && r[groupBy] === group,
      );
      if (!item) return 0;

      return metric === "total_amount"
        ? Number(item.total_amount)
        : item.total_count;
    }),
    color: groupBy === "status" ? STATUS_COLORS[group] : CHANNEL_COLORS[group],
  }));

  return { xLabels: months, series };
};

export const transformYearlyData = (
  results: any[],
  metric: "total_amount" | "total_count" = "total_amount",
  groupBy: "status" | "channel" = "status",
) => {
  const years = Array.from(
    new Set(results.map((r) => r.year_value)),
  ) as number[];
  years.sort((a, b) => a - b);

  const groups = Array.from(new Set(results.map((r) => r[groupBy])));

  const series = groups.map((group) => ({
    label: group,
    data: years.map((year) => {
      const item = results.find(
        (r) => r.year_value === year && r[groupBy] === group,
      );
      if (!item) return 0;

      return metric === "total_amount"
        ? Number(item.total_amount)
        : item.total_count;
    }),
    color: groupBy === "status" ? STATUS_COLORS[group] : CHANNEL_COLORS[group],
  }));

  const xLabels = years.map(String);

  return { xLabels, series };
};
